// app/javascript/components/imagePicker.js
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";

const EXT_TO_MIME = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
  heic: "image/heic",
  heif: "image/heif",
  txt: "text/plain",
  text: "text/plain",
  csv: "text/csv",
};
const ACCEPT_MIMES = new Set(Object.values(EXT_TO_MIME));
const CSV_MIMES = new Set(["text/csv", "application/csv", "application/vnd.ms-excel"]);
const TEXT_MIMES = new Set(["text/plain"]);
const MAX_FILES = 10;
const MAX_BYTES = 12 * 1024 * 1024; // 12MB

function guessMime(file) {
  if (file.type) return file.type;
  const name = file.name || "";
  const ext = name.split(".").pop()?.toLowerCase();
  if (ext && EXT_TO_MIME[ext]) return EXT_TO_MIME[ext];
  if (ext === "pdf") return "application/pdf";
  return ""; // unknown; allow image/* accept to still pick it up via input
}

function isCsvMime(mime) {
  return CSV_MIMES.has((mime || "").toString().toLowerCase());
}

function isTextMime(mime) {
  return TEXT_MIMES.has((mime || "").toString().toLowerCase());
}

async function computeSHA256(file) {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(hash)].map(b => b.toString(16).padStart(2, "0")).join("");
}

function getDims(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ url, w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => resolve({ url, w: null, h: null }); // HEIC in Chrome => dims likely null
    img.src = url;
  });
}

const ImagePicker = forwardRef(function ImagePicker({ onFiles }, ref) {
  const inputRef = useRef(null);
  const [previews, setPreviews] = useState([]); // [{file, url, width, height, sha256, mime}]
  const [dragOver, setDragOver] = useState(false);

  useImperativeHandle(ref, () => ({
    open: () => inputRef.current?.click(),
    ingest: (fileList) => takeFiles(fileList),
    clear: () => setPreviews(() => { onFiles?.([]); return []; }),
  }));

  const normalizeFiles = useCallback(async (filesLike) => {
    const files = Array.from(filesLike || []).slice(0, MAX_FILES);
    const usable = files.filter(f => {
      const mime = guessMime(f);
      const okSize = f.size <= MAX_BYTES;
      const okType = mime.startsWith("image/") || mime === "application/pdf" || isCsvMime(mime) || ACCEPT_MIMES.has(mime);
      return okType && okSize;
    });
    const enriched = [];
    for (const f of usable) {
      const mime = guessMime(f);
      const isPdf = mime === "application/pdf";
      const isCsv = isCsvMime(mime);
      const isText = isTextMime(mime);
      const [dims, sha] = await Promise.all([
        (isPdf || isCsv || isText) ? Promise.resolve({ url: null, w: null, h: null }) : getDims(f),
        computeSHA256(f)
      ]);

      enriched.push({
        file: f,
        url: dims.url,
        width: dims.w,
        height: dims.h,
        sha256: sha,
        mime,
      });
    }
    return enriched;
  }, []);

  const takeFiles = useCallback(async (filesLike) => {
    const enriched = await normalizeFiles(filesLike);
    const next = [...previews, ...enriched].slice(0, MAX_FILES);
    setPreviews(next);
    onFiles?.(next);
  }, [normalizeFiles, onFiles, previews]);

  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    await takeFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  const onPasteDoc = async (e) => {
    // If you mount this above a textarea, paste may hit the textarea.
    // Optionally keep this here as a convenience when focus is on the picker.
    const items = e.clipboardData?.items || [];
    const files = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      await takeFiles(files);
    }
  };

  const removeAt = (idx) => {
    const next = previews.slice(0, idx).concat(previews.slice(idx + 1));
    setPreviews(next);
    onFiles?.(next);
  };

  return (
    <Box
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onPaste={onPasteDoc}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 36,
        px: 0.5,
        borderRadius: 1,
        outline: dragOver ? "2px dashed #7aa8ff" : "none",
        outlineOffset: 2,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf,.txt,text/plain,.csv,text/csv"
        multiple
        style={{ display: "none" }}
        onChange={e => takeFiles(e.target.files)}
      />

      {/* Thumbnails */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {previews.map((p, idx) => (
          <Box key={idx} sx={{ position: "relative" }}>
           {p.mime === "application/pdf" || isCsvMime(p.mime) || isTextMime(p.mime) ? (
             <Box
               sx={{
                 width: 36, height: 36,
                 borderRadius: 1,
                 border: "1px solid #e0e0e0",
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "center",
                 fontSize: 10,
                 fontWeight: 700
               }}
             >
               {isCsvMime(p.mime) ? "CSV" : isTextMime(p.mime) ? "TXT" : "PDF"}
             </Box>
           ) : (
             <img
               src={p.url}
               alt={p.file.name}
               style={{
                 width: 36,
                 height: 36,
                 objectFit: "cover",
                 borderRadius: 6,
                 border: "1px solid #e0e0e0"
               }}
             />
           )}

            <Box
              onClick={() => removeAt(idx)}
              sx={{
                position: "absolute",
                top: -6,
                right: -6,
                width: 18,
                height: 18,
                lineHeight: "18px",
                borderRadius: "50%",
                textAlign: "center",
                fontSize: 12,
                background: "#111",
                color: "#fff",
                "&:hover": { cursor: "pointer", filter: "brightness(1.2)" }
              }}
              title="Remove"
            >×</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
});

export default ImagePicker;
